import { useChurchStore, type CurrentMembership, type SystemRole } from '@/stores/church-store';

/**
 * Console permission codes used by UI gates (command palette, search, page actions).
 * Server remains the source of truth — these only hide what the API would refuse.
 */
export const PERMISSIONS = {
  PEOPLE_VIEW: 'PEOPLE_VIEW',
  PEOPLE_CREATE: 'PEOPLE_CREATE',
  PEOPLE_EDIT: 'PEOPLE_EDIT',
  PEOPLE_DELETE: 'PEOPLE_DELETE',
  PEOPLE_MANAGE_STATUS: 'PEOPLE_MANAGE_STATUS',
  PEOPLE_MANAGE_DEPARTMENTS: 'PEOPLE_MANAGE_DEPARTMENTS',
  PEOPLE_MANAGE_MINISTERS: 'PEOPLE_MANAGE_MINISTERS',
  PEOPLE_MANAGE_COUNCIL: 'PEOPLE_MANAGE_COUNCIL',
  PEOPLE_IMPORT: 'PEOPLE_IMPORT',
  PEOPLE_EXPORT: 'PEOPLE_EXPORT',
  PEOPLE_VIEW_CONTACT: 'PEOPLE_VIEW_CONTACT',
  PEOPLE_VIEW_SECONDARY: 'PEOPLE_VIEW_SECONDARY',
  FOLLOWUP_VIEW: 'FOLLOWUP_VIEW',
  FOLLOWUP_ASSIGN: 'FOLLOWUP_ASSIGN',
  FOLLOWUP_COMPLETE: 'FOLLOWUP_COMPLETE',
  GROUPS_VIEW: 'GROUPS_VIEW',
  GROUPS_CREATE: 'GROUPS_CREATE',
  GROUPS_EDIT: 'GROUPS_EDIT',
  GROUPS_DELETE: 'GROUPS_DELETE',
  GROUPS_MANAGE_TEMPLATES: 'GROUPS_MANAGE_TEMPLATES',
  GROUPS_MANAGE_LEADERS: 'GROUPS_MANAGE_LEADERS',
  GROUPS_APPROVE_JOIN: 'GROUPS_APPROVE_JOIN',
  GROUPS_MESSAGE: 'GROUPS_MESSAGE',
  PROGRAMS_VIEW: 'PROGRAMS_VIEW',
  PROGRAMS_CREATE: 'PROGRAMS_CREATE',
  PROGRAMS_EDIT: 'PROGRAMS_EDIT',
  PROGRAMS_DELETE: 'PROGRAMS_DELETE',
  PROGRAMS_CANCEL_OCCURRENCE: 'PROGRAMS_CANCEL_OCCURRENCE',
  PROGRAMS_MANAGE_REGISTRATIONS: 'PROGRAMS_MANAGE_REGISTRATIONS',
  PROGRAMS_MANAGE_VOLUNTEERS: 'PROGRAMS_MANAGE_VOLUNTEERS',
  PROGRAMS_MANAGE_WORSHIP: 'PROGRAMS_MANAGE_WORSHIP',
  ATTENDANCE_VIEW: 'ATTENDANCE_VIEW',
  ATTENDANCE_MANAGE_SESSIONS: 'ATTENDANCE_MANAGE_SESSIONS',
  ATTENDANCE_CHECK_IN_OTHERS: 'ATTENDANCE_CHECK_IN_OTHERS',
  ATTENDANCE_MANAGE_KIOSKS: 'ATTENDANCE_MANAGE_KIOSKS',
  ATTENDANCE_VIEW_VISITORS: 'ATTENDANCE_VIEW_VISITORS',
  ATTENDANCE_EXPORT: 'ATTENDANCE_EXPORT',
  GIVING_VIEW: 'GIVING_VIEW',
  GIVING_VIEW_GIVER_IDENTITY: 'GIVING_VIEW_GIVER_IDENTITY',
  GIVING_RECORD: 'GIVING_RECORD',
  GIVING_EDIT: 'GIVING_EDIT',
  GIVING_VOID: 'GIVING_VOID',
  GIVING_MANAGE_BATCHES: 'GIVING_MANAGE_BATCHES',
  GIVING_MANAGE_FUNDS: 'GIVING_MANAGE_FUNDS',
  GIVING_MANAGE_PLEDGES: 'GIVING_MANAGE_PLEDGES',
  GIVING_CLOSE_PERIOD: 'GIVING_CLOSE_PERIOD',
  GIVING_ADJUST_BALANCE: 'GIVING_ADJUST_BALANCE',
  GIVING_EXPORT: 'GIVING_EXPORT',
  GIVING_VIEW_MEMBER_HISTORY: 'GIVING_VIEW_MEMBER_HISTORY',
  GIVING_ISSUE_STATEMENT: 'GIVING_ISSUE_STATEMENT',
  PROJECTS_VIEW: 'PROJECTS_VIEW',
  PROJECTS_CREATE: 'PROJECTS_CREATE',
  PROJECTS_EDIT: 'PROJECTS_EDIT',
  PROJECTS_PUBLISH: 'PROJECTS_PUBLISH',
  PROJECTS_CLOSE: 'PROJECTS_CLOSE',
  EXPENSES_VIEW: 'EXPENSES_VIEW',
  EXPENSES_CREATE: 'EXPENSES_CREATE',
  EXPENSES_EDIT: 'EXPENSES_EDIT',
  EXPENSES_APPROVE: 'EXPENSES_APPROVE',
  EXPENSES_VOID: 'EXPENSES_VOID',
  EXPENSES_MANAGE_CATEGORIES: 'EXPENSES_MANAGE_CATEGORIES',
  EXPENSES_EXPORT: 'EXPENSES_EXPORT',
  WELFARE_VIEW: 'WELFARE_VIEW',
  WELFARE_CREATE: 'WELFARE_CREATE',
  WELFARE_REVIEW: 'WELFARE_REVIEW',
  WELFARE_APPROVE: 'WELFARE_APPROVE',
  WELFARE_DISBURSE: 'WELFARE_DISBURSE',
  BUDGETS_VIEW: 'BUDGETS_VIEW',
  BUDGETS_CREATE: 'BUDGETS_CREATE',
  BUDGETS_EDIT: 'BUDGETS_EDIT',
  BUDGETS_APPROVE: 'BUDGETS_APPROVE',
  PAYMENTS_VIEW: 'PAYMENTS_VIEW',
  PAYMENTS_CONFIGURE: 'PAYMENTS_CONFIGURE',
  PAYMENTS_MANAGE_LINKS: 'PAYMENTS_MANAGE_LINKS',
  PAYMENTS_RECONCILE: 'PAYMENTS_RECONCILE',
  PAYMENTS_REFUND: 'PAYMENTS_REFUND',
  MESSAGING_VIEW: 'MESSAGING_VIEW',
  MESSAGING_SEND_PUSH: 'MESSAGING_SEND_PUSH',
  MESSAGING_SEND_SMS: 'MESSAGING_SEND_SMS',
  MESSAGING_SEND_EMAIL: 'MESSAGING_SEND_EMAIL',
  MESSAGING_MANAGE_LISTS: 'MESSAGING_MANAGE_LISTS',
  MESSAGING_CONFIGURE_SMS: 'MESSAGING_CONFIGURE_SMS',
  MESSAGING_MANAGE_TEMPLATES: 'MESSAGING_MANAGE_TEMPLATES',
  MESSAGING_MANAGE_AUTOMATIONS: 'MESSAGING_MANAGE_AUTOMATIONS',
  MESSAGING_MANAGE_CREDITS: 'MESSAGING_MANAGE_CREDITS',
  CONTENT_VIEW: 'CONTENT_VIEW',
  CONTENT_CREATE: 'CONTENT_CREATE',
  CONTENT_EDIT: 'CONTENT_EDIT',
  CONTENT_PUBLISH: 'CONTENT_PUBLISH',
  CONTENT_DELETE: 'CONTENT_DELETE',
  CONTENT_MANAGE_SERIES: 'CONTENT_MANAGE_SERIES',
  CONTENT_MANAGE_GALLERY: 'CONTENT_MANAGE_GALLERY',
  CONTENT_MODERATE: 'CONTENT_MODERATE',
  DOCUMENTS_VIEW: 'DOCUMENTS_VIEW',
  DOCUMENTS_CREATE: 'DOCUMENTS_CREATE',
  DOCUMENTS_EDIT: 'DOCUMENTS_EDIT',
  DOCUMENTS_DELETE: 'DOCUMENTS_DELETE',
  DOCUMENTS_MANAGE: 'DOCUMENTS_MANAGE',
  ASSETS_VIEW: 'ASSETS_VIEW',
  ASSETS_CREATE: 'ASSETS_CREATE',
  ASSETS_EDIT: 'ASSETS_EDIT',
  ASSETS_DISPOSE: 'ASSETS_DISPOSE',
  ASSETS_MANAGE_RENTALS: 'ASSETS_MANAGE_RENTALS',
  SACRAMENTS_VIEW: 'SACRAMENTS_VIEW',
  SACRAMENTS_RECORD: 'SACRAMENTS_RECORD',
  SACRAMENTS_EDIT: 'SACRAMENTS_EDIT',
  SACRAMENTS_EXPORT: 'SACRAMENTS_EXPORT',
  PRAYER_VIEW: 'PRAYER_VIEW',
  PRAYER_ASSIGN: 'PRAYER_ASSIGN',
  PRAYER_RESPOND: 'PRAYER_RESPOND',
  PRAYER_ARCHIVE: 'PRAYER_ARCHIVE',
  PASTORAL_VIEW: 'PASTORAL_VIEW',
  PASTORAL_RECORD_VISIT: 'PASTORAL_RECORD_VISIT',
  PASTORAL_RECORD_COUNSELING: 'PASTORAL_RECORD_COUNSELING',
  PASTORAL_VIEW_CONFIDENTIAL: 'PASTORAL_VIEW_CONFIDENTIAL',
  PASTORAL_MANAGE_APPOINTMENTS: 'PASTORAL_MANAGE_APPOINTMENTS',
  EVANGELISM_VIEW: 'EVANGELISM_VIEW',
  EVANGELISM_RECORD: 'EVANGELISM_RECORD',
  EVANGELISM_EDIT: 'EVANGELISM_EDIT',
  EVANGELISM_ASSIGN_FOLLOW_UP: 'EVANGELISM_ASSIGN_FOLLOW_UP',
  COMMUNITY_VIEW: 'COMMUNITY_VIEW',
  COMMUNITY_POST: 'COMMUNITY_POST',
  COMMUNITY_MODERATE: 'COMMUNITY_MODERATE',
  REPORTS_VIEW: 'REPORTS_VIEW',
  REPORTS_VIEW_FINANCIAL: 'REPORTS_VIEW_FINANCIAL',
  REPORTS_VIEW_PEOPLE: 'REPORTS_VIEW_PEOPLE',
  REPORTS_VIEW_ATTENDANCE: 'REPORTS_VIEW_ATTENDANCE',
  REPORTS_VIEW_GROUPS: 'REPORTS_VIEW_GROUPS',
  REPORTS_VIEW_PROGRAMS: 'REPORTS_VIEW_PROGRAMS',
  REPORTS_VIEW_MESSAGING: 'REPORTS_VIEW_MESSAGING',
  REPORTS_VIEW_GIVING: 'REPORTS_VIEW_GIVING',
  REPORTS_VIEW_SPENDING: 'REPORTS_VIEW_SPENDING',
  REPORTS_VIEW_PROJECTS: 'REPORTS_VIEW_PROJECTS',
  REPORTS_VIEW_WELFARE: 'REPORTS_VIEW_WELFARE',
  REPORTS_VIEW_ASSETS: 'REPORTS_VIEW_ASSETS',
  REPORTS_VIEW_PASTORAL: 'REPORTS_VIEW_PASTORAL',
  REPORTS_VIEW_PRAYER: 'REPORTS_VIEW_PRAYER',
  REPORTS_CREATE: 'REPORTS_CREATE',
  REPORTS_EXPORT: 'REPORTS_EXPORT',
  DASHBOARDS_VIEW: 'DASHBOARDS_VIEW',
  DASHBOARDS_MANAGE: 'DASHBOARDS_MANAGE',
  BRANCHES_VIEW: 'BRANCHES_VIEW',
  BRANCHES_MANAGE: 'BRANCHES_MANAGE',
  BRANCHES_SWITCH: 'BRANCHES_SWITCH',
  BRANCHES_VIEW_CONSOLIDATED: 'BRANCHES_VIEW_CONSOLIDATED',
  BRANDING_VIEW: 'BRANDING_VIEW',
  BRANDING_MANAGE: 'BRANDING_MANAGE',
  AUDIT_VIEW: 'AUDIT_VIEW',
  AUDIT_EXPORT: 'AUDIT_EXPORT',
  SUPPORT_TICKETS_VIEW: 'SUPPORT_TICKETS_VIEW',
  SUPPORT_TICKETS_CREATE: 'SUPPORT_TICKETS_CREATE',
  SUPPORT_TICKETS_REPLY: 'SUPPORT_TICKETS_REPLY',
} as const;

export type PermissionCode = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];

type MembershipLike = Pick<CurrentMembership, 'systemRole' | 'permissions'> | null | undefined;

export const isAdminOrOwner = (membership: MembershipLike): boolean =>
  membership?.systemRole === 'owner' || membership?.systemRole === 'admin';

export const hasPermission = (
  membership: MembershipLike,
  permission: PermissionCode | string,
): boolean => {
  if (isAdminOrOwner(membership)) return true;
  return membership?.permissions.includes(permission) ?? false;
};

export const hasAnyPermission = (
  membership: MembershipLike,
  permissions: readonly (PermissionCode | string)[],
): boolean => {
  if (isAdminOrOwner(membership)) return true;
  return permissions.some((permission) => membership?.permissions.includes(permission) ?? false);
};

export const hasAllPermissions = (
  membership: MembershipLike,
  permissions: readonly (PermissionCode | string)[],
): boolean => {
  if (isAdminOrOwner(membership)) return true;
  return permissions.every((permission) => membership?.permissions.includes(permission) ?? false);
};

export const useCurrentMembership = (): CurrentMembership | null =>
  useChurchStore((state) => state.membership);

export type { SystemRole };
